import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SingleFunnel, FunnelColors } from "./SingleFunnel";
import { cn } from "@/lib/utils";
import {
  FunnelBarData,
  FunnelChartState,
  CommunityStatus,
} from "@/components/Funnel/types";
import { Skeleton } from "../ui";
import { ViewMode } from "@/pages/ZeemeeFunnel/types";
import { FilterPills } from "./FilterPills";
import { FilterState } from "../FilterSidebar/FilterState";
import { FilterName } from "../FilterSidebar/types";

const BAR_HEIGHT = 40;
const WEDGE_HEIGHT = 16;
const GRID_CHANGE_ANIMATION_DURATION = 300;
const GRID_LINE_COUNT = 5;
const ROWS_COUNT = 5;

export type CommunityStatusSelectOption = CommunityStatus | "both";
export type Funnel = "left" | "right";

const LEFT_FUNNEL_COLORS: FunnelColors = {
  rightBarColor: "var(--funnel-1-right)",
  leftBarColor: "var(--funnel-1-left)",
  rightWedgeColor: "var(--funnel-1-right-wedge)",
  leftWedgeColor: "var(--funnel-1-left-wedge)",
  percentColor: "var(--funnel-1-percent-right)",
  pillBackgroundColor: "var(--funnel-1-pill-background)",
};

const RIGHT_FUNNEL_COLORS: FunnelColors = {
  rightBarColor: "var(--funnel-2-right)",
  leftBarColor: "var(--funnel-2-left)",
  rightWedgeColor: "var(--funnel-2-right-wedge)",
  leftWedgeColor: "var(--funnel-2-left-wedge)",
  percentColor: "var(--funnel-2-percent-right)",
  pillBackgroundColor: "var(--funnel-2-pill-background)",
};

export interface FunnelData {
  inCommunity: FunnelBarData[];
  notInCommunity: FunnelBarData[];
}

export interface SelectedFunnelBar {
  stage: FunnelChartState;
  communityStatus: CommunityStatusSelectOption;
  funnel: Funnel;
}

interface Props {
  // Left Funnel
  isLoading?: boolean;
  filterState?: FilterState;
  funnelData: FunnelData;

  // Right Funnel
  isCompareLoading?: boolean;
  compareFunnelData: FunnelData | undefined;
  compareFilterState?: FilterState | undefined;

  // Selection
  selectedBar: SelectedFunnelBar | null;
  onBarClick?: (selectedBar: SelectedFunnelBar) => void;
  allowNotInCommunitySelection?: boolean;

  // Filters
  onRemoveFilter?: (name: FilterName, side: Funnel) => void;
  onAddFilter?: (side: Funnel) => void;
  showFilterPills?: boolean;
  focusedFunnel: "left" | "right" | null;
}

export const FunnelsChart: React.FC<Props> = ({
  isLoading = false,
  isCompareLoading = false,
  funnelData,
  compareFunnelData,
  compareFilterState,
  onBarClick,
  selectedBar,
  onRemoveFilter,
  onAddFilter,
  filterState,
  showFilterPills,
  focusedFunnel,
  allowNotInCommunitySelection,
}) => {
  const stages: FunnelChartState[][] = [
    ["inquired"],
    ["applied"],
    ["admitted"],
    ["committed"],
    ["enrolled", "melted"],
  ];
  const [focusedFunnelState, setFocusedFunnelState] = useState(focusedFunnel);
  const [viewMode, setViewMode] = useState<ViewMode>("enrolled");
  const { t } = useTranslation();

  useEffect(() => {
    if (focusedFunnel === null) {
      setTimeout(() => {
        setFocusedFunnelState(focusedFunnel);
      }, GRID_CHANGE_ANIMATION_DURATION);
    } else {
      setFocusedFunnelState(focusedFunnel);
    }
  }, [focusedFunnel]);

  const [rowsLeft, rowsRight] = useMemo(() => {
    return [
      getFilterRows(funnelData.inCommunity, viewMode),
      getFilterRows(funnelData.notInCommunity, viewMode),
    ];
  }, [funnelData, viewMode]);

  const [compareRowsLeft, compareRowsRight] = useMemo(() => {
    return [
      getFilterRows(compareFunnelData?.inCommunity || [], viewMode),
      getFilterRows(compareFunnelData?.notInCommunity || [], viewMode),
    ];
  }, [compareFunnelData, viewMode]);

  const onLeftFunnelBarClick = useCallback(
    (stage: FunnelChartState, communityStatus: CommunityStatus) => {
      if (onBarClick) onBarClick({ stage, communityStatus, funnel: "left" });
    },
    [onBarClick],
  );

  const onRightFunnelBarClick = useCallback(
    (stage: FunnelChartState, communityStatus: CommunityStatus) => {
      if (onBarClick) onBarClick({ stage, communityStatus, funnel: "right" });
    },
    [onBarClick],
  );

  let gridCols = "grid-cols-[auto_1fr_30px_1fr]";
  if (focusedFunnel === "left") gridCols = "grid-cols-[auto_1fr_0px_0fr]";
  if (focusedFunnel === "right") gridCols = "grid-cols-[auto_0fr_0px_1fr]";

  return (
    <div
      className={cn("relative grid gap-y-6", gridCols)}
      style={{
        transition: `grid-template-columns ${GRID_CHANGE_ANIMATION_DURATION}ms ease`,
      }}
    >
      <div className="row-start-1">
        {focusedFunnelState === null && (
          <div className="mr-2 text-right font-semibold text-muted-foreground uppercase opacity-50">
            {t("global.filters")}
          </div>
        )}
      </div>
      <div className="col-start-2 col-end-3 row-start-1 flex justify-center">
        {showFilterPills && focusedFunnelState !== "right" && (
          <FilterPills
            filterState={filterState}
            showTitle={focusedFunnelState !== null}
            onFilterRemove={(name) =>
              onRemoveFilter && onRemoveFilter(name, "left")
            }
            onAddFilter={() => onAddFilter && onAddFilter("left")}
          />
        )}
      </div>
      <div></div>
      <div className="col-start-4 col-end-5 row-start-1 flex justify-center">
        {showFilterPills && focusedFunnelState !== "left" && (
          <FilterPills
            filterState={compareFilterState}
            onFilterRemove={(name) =>
              onRemoveFilter && onRemoveFilter(name, "right")
            }
            onAddFilter={() => onAddFilter && onAddFilter("right")}
          />
        )}
      </div>
      <div className="z-1 col-start-1 col-end-2 row-start-2 row-end-3 mr-6 pt-7">
        <StageLabelsColumn
          stages={stages}
          isLoading={isLoading}
          onStageSelect={(stage) => setViewMode(stage as ViewMode)}
          selected={viewMode}
        />
      </div>
      <div className="col-start-2 col-end-3 row-start-2 row-end-3 flex justify-center">
        {focusedFunnelState !== "right" && (
          <SingleFunnel
            isLoading={isLoading}
            inCommunity={rowsLeft}
            notInCommunity={rowsRight}
            colors={LEFT_FUNNEL_COLORS}
            onBarClick={onLeftFunnelBarClick}
            fade={selectedBar?.funnel === "right"}
            allowNotInCommunitySelection={allowNotInCommunitySelection}
            selectedStage={
              selectedBar?.funnel === "left" ? selectedBar.stage : undefined
            }
            selectedCommunityStatus={
              selectedBar?.funnel === "left"
                ? selectedBar.communityStatus
                : undefined
            }
            testId="funnel-left"
          />
        )}
      </div>
      <div></div>
      <div className="col-start-4 col-end-5 row-start-2 row-end-3 flex justify-center">
        {focusedFunnelState !== "left" && (
          <SingleFunnel
            isLoading={isCompareLoading}
            inCommunity={compareRowsLeft}
            notInCommunity={compareRowsRight}
            colors={RIGHT_FUNNEL_COLORS}
            onBarClick={onRightFunnelBarClick}
            allowNotInCommunitySelection={allowNotInCommunitySelection}
            fade={selectedBar?.funnel === "left"}
            selectedStage={
              selectedBar?.funnel === "right" ? selectedBar.stage : undefined
            }
            selectedCommunityStatus={
              selectedBar?.funnel === "right"
                ? selectedBar.communityStatus
                : undefined
            }
            testId="funnel-right"
          />
        )}
      </div>
      <div className="col-span-full row-start-2 row-end-3 pt-[30px]">
        <GridLines count={GRID_LINE_COUNT} />
      </div>
    </div>
  );
};

const StageLabelsColumn = ({
  stages,
  selected,
  isLoading,
  onStageSelect,
}: {
  stages: FunnelChartState[][];
  selected: FunnelChartState;
  isLoading: boolean;
  onStageSelect: (state: FunnelChartState) => void;
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <>
        {Array.from({ length: ROWS_COUNT }).map((_, i) => {
          return (
            <div
              key={`${i}-label`}
              className="flex items-end pb-1"
              style={{
                height: i === 0 ? BAR_HEIGHT : BAR_HEIGHT + WEDGE_HEIGHT,
              }}
            >
              <Skeleton className="h-4 w-30"></Skeleton>
            </div>
          );
        })}
      </>
    );
  }

  return stages.map((data, idx) => {
    const isFirst = idx === 0;
    if (data.length > 1) {
      return (
        <div
          key={idx}
          className="flex items-end pb-1 text-sm"
          style={{
            height: isFirst ? BAR_HEIGHT : BAR_HEIGHT + WEDGE_HEIGHT,
          }}
        >
          {data.map((stageName, idx) => (
            <div key={idx}>
              <button
                onClick={() => onStageSelect(stageName)}
                className={selected === stageName ? "" : "opacity-30"}
              >
                {t("funnelGroups." + stageName)}
              </button>
              {idx !== data.length - 1 && <span className="mx-2"> / </span>}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        key={idx}
        className="flex items-end pb-1 text-sm"
        style={{
          height: isFirst ? BAR_HEIGHT : BAR_HEIGHT + WEDGE_HEIGHT,
        }}
      >
        {data.map((stageName) => t("funnelGroups." + stageName))}
      </div>
    );
  });
};

const GridLines = ({ count }: { count: number }) => {
  if (count < 0) return;
  return (
    <>
      {new Array(count).fill(null).map((_, idx) => {
        const isFirst = idx === 0;
        return (
          <div
            key={idx}
            className={`border-b border-border`}
            style={{
              height: isFirst ? BAR_HEIGHT : BAR_HEIGHT + WEDGE_HEIGHT,
            }}
          ></div>
        );
      })}
    </>
  );
};

const getFilterRows = (data: FunnelBarData[], viewMode: ViewMode) => {
  return data.filter((data) =>
    ["enrolled", "melted"].includes(data.stage)
      ? data.stage === viewMode
      : true,
  );
};
